import { StructClass } from '@/shared/gen/_framework/reified'
import { round, ClmmPool } from '@kunalabs-io/kai'
import { DefaultTheme } from 'styled-components'
import { PairInfo } from 'utils/pair-info'

export const ellipsizeTokenAddress = (tokenAddress: string, charStartEndNum = 5) => {
  if (tokenAddress.length <= charStartEndNum * 2) {
    return tokenAddress
  }
  return `${tokenAddress.substring(0, charStartEndNum)}...${tokenAddress.substring(
    tokenAddress.length - charStartEndNum,
    tokenAddress.length
  )}`
}

export function formatNumberWithCommas(num: number, decimals: number): string {
  num = round(num, decimals)
  return num.toLocaleString('en-US', { maximumFractionDigits: decimals })
}

export const getHealthColor = (percentage: number, theme: DefaultTheme) => {
  switch (true) {
    case percentage <= 25:
      return theme.colors.text.danger
    case percentage > 25 && percentage <= 50:
      return theme.colors.text.danger1
    case percentage > 50 && percentage <= 75:
      return theme.colors.text.danger2
    default:
      return theme.colors.text.success
  }
}

export const getPositionStatusLabel = (status: 'active' | 'inactive' | 'closed') => {
  switch (status) {
    case 'active':
      return 'Open'
    case 'inactive':
      return 'Inactive'
    default:
      return 'Closed'
  }
}

export const getPositionStatusBgColor = (
  status: 'active' | 'inactive' | 'closed',
  theme: DefaultTheme
) => {
  switch (status) {
    case 'active':
      return theme.colors.background.success
    case 'inactive':
      return 'transparent'
    default:
      return theme.colors.text.danger
  }
}

export const getPositionStatusColor = (
  status: 'active' | 'inactive' | 'closed',
  theme: DefaultTheme
) => {
  if (status === 'inactive') {
    return theme.colors.text.primary
  }

  return theme.colors.text.primaryStatusBg
}

export const getOrdinalSuffix = (day: number) => {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export const isSameDay = (timestamp1: number, timestamp2: number) => {
  const d1 = new Date(timestamp1)
  const d2 = new Date(timestamp2)

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  )
}

export const getDisplayTimestamp = (timestamp: number) => {
  const date = new Date(timestamp)

  const day = date.getDate()
  const month = date.toLocaleString('default', { month: 'short' })
  const year = date.getFullYear()
  const hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')

  const ordinalSuffix = getOrdinalSuffix(day)

  const currentYear = new Date().getFullYear()

  const eventYear = date.getFullYear()

  let formattedDate = ''

  if (currentYear === eventYear) {
    formattedDate = `${day}${ordinalSuffix} ${month} ${hours}:${minutes}`.trim()
  } else {
    formattedDate = `${day}${ordinalSuffix} ${month} ${year} ${hours}:${minutes}`.trim()
  }

  return formattedDate
}

export const getIsReversedPair = (
  pair: PairInfo<string>,
  pool?: ClmmPool<StructClass, unknown, string, string>
) => {
  if (!pool) {
    return false
  }

  if (pair.coinX.typeName === pool.X.typeName && pair.coinY.typeName === pool.Y.typeName) {
    return false
  }

  return true
}

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
